import { ReactNode, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

interface LayoutProps {
  children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  
  const isActive = (path: string) => location.pathname === path
  
  const handleLogout = async () => {
    await logout()
    navigate('/app')
    setIsMobileMenuOpen(false)
  }
  
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }
  
  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      {user && (
        <header className="bg-white border-b border-gray-200 shadow-sm">
          <nav className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
            {/* Logo */}
            <Link to="/app/dashboard" className="flex items-center gap-2 text-blue-600 text-2xl font-bold no-underline">
              <svg width="32" height="32" viewBox="0 0 600 530" fill="none">
                <path 
                  d="M135.72 44.03c66.496 49.921 138.02 151.14 164.28 205.46 26.262-54.316 97.782-155.54 164.28-205.46 47.98-36.021 125.72-63.892 125.72 24.795 0 17.712-10.155 148.79-16.111 170.07-20.703 73.984-96.144 92.854-163.25 81.433 117.3 19.964 147.14 86.092 82.697 152.22-122.39 125.59-175.91-31.511-189.63-71.766-2.514-7.3797-3.6904-10.832-3.7077-7.8964-.0174-2.9357-1.1937.51669-3.7077 7.8964-13.714 40.255-67.233 197.36-189.63 71.766-64.444-66.128-34.605-132.26 82.697-152.22-67.108 11.421-142.55-7.4491-163.25-81.433-5.9562-21.282-16.111-152.36-16.111-170.07 0-88.687 77.742-60.816 125.72-24.795z" 
                  fill="currentColor"
                />
              </svg>
              <span className="hidden sm:inline">Skeet Deleter</span>
            </Link>
            
            {/* Desktop Navigation - Hidden on mobile */}
            <div className="hidden md:flex items-center gap-8">
              <Link 
                to="/app/dashboard"
                className={`no-underline ${
                  isActive('/app/dashboard') 
                    ? 'text-blue-600 font-semibold border-b-2 border-blue-600 pb-1' 
                    : 'text-gray-600'
                }`}
              >
                Dashboard
              </Link>
              
              <div className="flex items-center gap-2">
                {user.image_url ? (
                  <img 
                    src={user.image_url} 
                    alt={user.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
                    <svg width="24" height="24" fill="#666" viewBox="0 0 24 24">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                    </svg>
                  </div>
                )}
                <div className="text-left">
                  <div className="text-sm font-semibold text-gray-900">{user.name}</div>
                  <div className="text-xs text-gray-600">@{user.handle}</div>
                </div>
                <button
                  onClick={handleLogout}
                  className="ml-2 px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm text-gray-900 hover:bg-gray-200 transition-colors"
                >
                  Logout
                </button>
              </div>
            </div>
            
            {/* Hamburger Button - Visible only on mobile */}
            <button 
              onClick={toggleMobileMenu}
              className="md:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </nav>
          
          {/* Mobile Menu Dropdown */}
          {isMobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200 bg-white">
              <div className="px-4 py-3 space-y-3">
                {/* User Info */}
                <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
                  {user.image_url ? (
                    <img 
                      src={user.image_url} 
                      alt={user.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center">
                      <svg width="24" height="24" fill="#666" viewBox="0 0 24 24">
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                      </svg>
                    </div>
                  )}
                  <div>
                    <div className="text-base font-semibold text-gray-900">{user.name}</div>
                    <div className="text-sm text-gray-600">@{user.handle}</div>
                  </div>
                </div>
                
                {/* Navigation Links */}
                <Link 
                  to="/app/dashboard"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block py-2 px-3 rounded-lg no-underline text-base ${
                    isActive('/app/dashboard') 
                      ? 'bg-blue-50 text-blue-600 font-semibold' 
                      : 'text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  Dashboard
                </Link>
                
                {/* Logout Button */}
                <button
                  onClick={handleLogout}
                  className="w-full text-left py-2 px-3 rounded-lg text-base text-red-600 hover:bg-red-50 transition-colors"
                >
                  Logout
                </button>
              </div>
            </div>
          )}
        </header>
      )}
      
      <main className="flex-1">
        {children}
      </main>
    </div>
  )
}

